import { faRobot } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  createStyles,
  darken,
  Link,
  makeStyles,
  TableCell,
  TableRow,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { yellow } from '@material-ui/core/colors';
import { orderBy } from 'lodash';
import React from 'react';
import VoxelChip from '../../../../../components/voxel/Chips/VoxelChip';
import TableCellTagGroups, {
  FastTagGroup,
} from '../../../../../components/muiLegacy/Tables/Shared/TableCellTagGroups';
import { ContentActionGq } from '../../../../../graphql/projects/models/ContentActionGq';
import { ContentTaskGq } from '../../../../../graphql/projects/models/ContentTaskGq';
import { QueuedItemStatus } from '../../../../../graphql/projects/models/QueuedItemStatus';
import { CurrentUserGq } from '../../../../../graphql/users/models/CurrentUserGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { DisplaySerpResult } from '../../../../DataManagementPage/components/Dialogs/SerpResultsDialog';
import ApproveContentButton from '../../Buttons/ContentTaskTable/ApproveContentButton';
import TableCellCheckIcon from './TableCellCheckIcon';
import TableCellUrl from './TableCellUrl';
import TaskAction from './TaskAction';
import { ColumnsState } from './useVisibleColumns';

export interface ContentTaskTableRowProps {
  columnState: ColumnsState;
  selected: boolean;
  setSelectedContentTask: (contentTask: ContentTaskGq) => void;
  shownTagGroups: FastTagGroup[];
  contentTask: ContentTaskGq;
  clientUrl: string;
  reload: () => Promise<any>;
  disabled: boolean;
  setDisplaySerpResult: (state: DisplaySerpResult) => void;
  user?: CurrentUserGq;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    tableRow: { cursor: 'pointer' },
    uploadInput: { display: 'none' },
    keywordTableCell: {
      maxWidth: '250px',
    },
    rankTableCell: {
      width: '118px',
    },
    cellOverflow: {
      boxSizing: 'border-box',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    unApprovedIcon: {
      color: theme.palette.grey[300],
      '&:hover': {
        color: theme.palette.grey[500],
      },
    },
    approvedIcon: {
      color: theme.palette.grey[600],
      '&:hover': {
        color: theme.palette.grey[600],
      },
    },
    naText: {
      color: theme.palette.grey[400],
    },
    neutralChip: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
      fontSize: '13px',
    },
    warningChip: {
      backgroundColor: yellow[100],
      color: darken(yellow[900], 0.2),
      fontSize: '13px',
    },
  })
);

// The entire row is memoized
export const ContentTaskTableRow: React.FC<ContentTaskTableRowProps> = React.memo(
  ({
    contentTask,
    setSelectedContentTask,
    clientUrl,
    selected,
    reload,
    columnState,
    shownTagGroups,
    disabled,
    setDisplaySerpResult,
    user,
  }) => {
    const orderedKeywords = orderBy(contentTask.keywords, [(c) => c.order, (c) => c.id]);
    const primaryKeyword = orderedKeywords[0];
    const secondaryKeyword = orderedKeywords[1];
    const otherKeywords = orderedKeywords.slice(2, 5);
    const moreKeywords = orderedKeywords.slice(5);
    const totalVolume = orderedKeywords.reduce((prev, kw) => prev + kw.volume ?? 0, 0);
    const totalTraffic = orderedKeywords.reduce((prev, kw) => prev + kw.traffic ?? 0, 0);

    //Content task dialog
    const openContentTaskDialog = (contentTask: ContentTaskGq) => {
      setSelectedContentTask(contentTask);
    };
    // Styling
    const theme = useTheme();
    const classes = useClasses(theme);

    return (
      <TableRow
        selected={selected}
        className={!disabled ? classes.tableRow : ''}
        onClick={!disabled ? () => openContentTaskDialog(contentTask) : () => { }}
        hover={!disabled}
      >
        {columnState.url && <TableCellUrl contentTask={contentTask} clientUrl={clientUrl} />}
        {columnState.approved && (
          <TableCell padding="checkbox" align="center">
            <ApproveContentButton
              contentTask={contentTask}
              reload={reload}
              iconSize={'small'}
              table={true}
              disabled={disabled}
            />
          </TableCell>
        )}
        {columnState.action && (
          <TableCell align="center">
            <TaskAction action={contentTask.actionFinal} table={true} size={'small'} />
          </TableCell>
        )}
        {!!shownTagGroups?.length && (
          <TableCellTagGroups
            tagIds={contentTask.tagCombinations
              .flatMap((tc) => tc.tags)
              .filter((tg) => !!tg.tagId)
              .map((tg) => tg.tagId as number)}
            tagGroups={shownTagGroups}
          />
        )}
        {columnState.statusCode && (
          <TableCell align="center">
            {contentTask.scrapedUrl?.pageHttpStatus && (
              <div className="w-fit mx-auto">
                <VoxelChip
                  title={contentTask.scrapedUrl?.pageHttpStatus}
                  color={contentTask.scrapedUrl?.pageHttpStatus >= 300 ? 'warning' : 'neutral'}
                />
              </div>
            )}
          </TableCell>
        )}
        {columnState.chatGpt && (
          <TableCell align="center">
            {contentTask.chatGptStatus && (
              <FontAwesomeIcon
                className={
                  contentTask.chatGptStatus === QueuedItemStatus.SUCCEEDED
                    ? 'text-success-600/70'
                    : contentTask.chatGptStatus === QueuedItemStatus.PROCESSING
                      ? 'text-warning-600/70'
                      : 'text-voxel-gray-400'
                }
                icon={faRobot}
                size="lg"
              />
            )}
          </TableCell>
        )}
        {columnState.totalVolume && (
          <TableCell align="right">{formatNumber(totalVolume)}</TableCell>
        )}
        {columnState.totalTraffic && (
          <TableCell align="right">
            {contentTask.actionFinal === ContentActionGq.CREATE ? (
              <Typography variant={'body2'} className={classes.naText}>
                N/A
              </Typography>
            ) : (
              formatNumber(totalTraffic)
            )}
          </TableCell>
        )}
        {columnState.primary.keyword && (
          <TableCell className={classes.keywordTableCell}>
            <Box className={classes.cellOverflow}>{primaryKeyword?.keyword}</Box>
          </TableCell>
        )}
        {columnState.primary.englishTranslation && (
          <TableCell className={classes.keywordTableCell} align="left">
            <Box className={classes.cellOverflow}>{primaryKeyword?.keywordEnglish}</Box>
          </TableCell>
        )}
        {columnState.primary.inTitle && (
          <TableCellCheckIcon
            checked={primaryKeyword?.inTitle}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.primary.inh1 && (
          <TableCellCheckIcon
            checked={primaryKeyword?.inH1}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.primary.inContent && (
          <TableCellCheckIcon
            checked={primaryKeyword?.inContent}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.primary.rank && (
          <TableCell className={classes.rankTableCell} align="right">
            {contentTask.actionFinal === ContentActionGq.CREATE ? (
              <Typography variant={'body2'} className={classes.naText}>
                N/A
              </Typography>
            ) : (
              <Link
                component={'button'}
                variant={'body2'}
                color={'textPrimary'}
                underline="hover"
                onClick={(event: React.MouseEvent<any>) => {
                  event.stopPropagation();
                  setDisplaySerpResult({
                    keywordId: primaryKeyword.keywordId,
                    rankNumber: primaryKeyword?.rank,
                  });
                }}
                onMouseDown={(event: React.MouseEvent<any>) => {
                  event.stopPropagation();
                }}
              >
                {primaryKeyword?.rank ?? '100+'}
              </Link>
            )}
          </TableCell>
        )}
        {columnState.primary.volume && (
          <TableCell align="right">{formatNumber(primaryKeyword?.volume)}</TableCell>
        )}
        {columnState.primary.traffic && (
          <TableCell align="right">
            {contentTask.actionFinal === ContentActionGq.CREATE ? (
              <Typography variant={'body2'} className={classes.naText}>
                N/A
              </Typography>
            ) : (
              <>{!!primaryKeyword.traffic ? formatNumber(primaryKeyword?.traffic) : '0'}</>
            )}
          </TableCell>
        )}
        {columnState.secondary.keyword && (
          <TableCell className={classes.keywordTableCell}>
            <Box className={classes.cellOverflow}>{secondaryKeyword?.keyword}</Box>
          </TableCell>
        )}
        {columnState.secondary.englishTranslation && (
          <TableCell className={classes.keywordTableCell}>
            <Box className={classes.cellOverflow}>{secondaryKeyword?.keywordEnglish}</Box>
          </TableCell>
        )}
        {columnState.secondary.inTitle && (
          <TableCellCheckIcon
            checked={secondaryKeyword?.inTitle}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.secondary.inh1 && (
          <TableCellCheckIcon
            checked={secondaryKeyword?.inH1}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.secondary.inContent && (
          <TableCellCheckIcon
            checked={secondaryKeyword?.inContent}
            contentAction={contentTask.actionFinal}
          />
        )}
        {columnState.secondary.rank && (
          <TableCell className={classes.rankTableCell} align="right">
            {contentTask.actionFinal === ContentActionGq.CREATE ? (
              <Typography variant={'body2'} className={classes.naText}>
                N/A
              </Typography>
            ) : (
              <Link
                component={'button'}
                variant={'body2'}
                color={'textPrimary'}
                underline="hover"
                onClick={(event: React.MouseEvent<any>) => {
                  event.stopPropagation();
                  setDisplaySerpResult({
                    keywordId: secondaryKeyword.keywordId,
                    rankNumber: secondaryKeyword?.rank,
                  });
                }}
                onMouseDown={(event: React.MouseEvent<any>) => {
                  event.stopPropagation();
                }}
              >
                {secondaryKeyword?.rank ?? '100+'}
              </Link>
            )}
          </TableCell>
        )}
        {columnState.secondary.volume && (
          <TableCell align="right">{formatNumber(secondaryKeyword?.volume)}</TableCell>
        )}
        {columnState.secondary.traffic && (
          <TableCell align="right">
            {contentTask.actionFinal === ContentActionGq.CREATE ? (
              <Typography variant={'body2'} className={classes.naText}>
                N/A
              </Typography>
            ) : (
              <>{!!secondaryKeyword?.traffic ? formatNumber(secondaryKeyword?.traffic) : '0'}</>
            )}
          </TableCell>
        )}
        {columnState.longTailKeywords && (
          <TableCell>
            {otherKeywords.map((ok) => ok.keyword).join(', ')}{' '}
            {!!moreKeywords.length ? ' ..+' + moreKeywords.length : ''}
          </TableCell>
        )}
      </TableRow>
    );
  }
);

export default ContentTaskTableRow;
